import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { Constants } from './app/core/helpers/constants';

type InstanceKey = 'ffm' | 'prspr' | 'cpjp';

function normalizeInstance(raw: string | null | undefined): InstanceKey | null {
  const v = (raw || '').toLowerCase().trim();
  if (v === 'ffm' || v === 'prspr' || v === 'cpjp') return v;
  return null;
}

function originFromEnv(url: string | undefined): string | null {
  const trimmed = (url || '').trim();
  if (!trimmed) return null;
  try {
    return new URL(trimmed, window.location.href).origin;
  } catch {
    return null;
  }
}

function inferInstanceFromUrl(): InstanceKey | null {
  try {
    const instParam = normalizeInstance(new URL(window.location.href).searchParams.get('inst'));
    if (instParam) return instParam;
  } catch {
    // ignore
  }

  // Local dev convention: ports map to instances
  switch (window.location.port) {
    case '4202':
      return 'prspr';
    case '4204':
      return 'cpjp';
    case '4200':
      return 'ffm';
  }

  const currentOrigin = window.location.origin;

  const prsprOrigin = originFromEnv(environment.PrsprFrontendUrl);
  if (prsprOrigin && prsprOrigin === currentOrigin) return 'prspr';

  const cpjpOrigin = originFromEnv(environment.cpjpFrontendUrl);
  if (cpjpOrigin && cpjpOrigin === currentOrigin) return 'cpjp';

  const ffmOrigin = originFromEnv(environment.ffmFrontendUrl);
  if (ffmOrigin && ffmOrigin === currentOrigin) return 'ffm';

  return null;
}

function ensureInstanceBeforeBootstrap(): void {
  try {
    const stored = normalizeInstance(localStorage.getItem(Constants.LS_CURRENT_INSTANCE));
    const inferred = inferInstanceFromUrl();

    // Prefer inferred instance for this origin; stored can be stale if localStorage was cleared mid-flow.
    if (inferred && stored !== inferred) {
      localStorage.setItem(Constants.LS_CURRENT_INSTANCE, inferred);
      return;
    }

    if (!stored) {
      localStorage.setItem(Constants.LS_CURRENT_INSTANCE, inferred ?? 'ffm');
    }
  } catch {
    // ignore (private mode / storage disabled)
  }
}

ensureInstanceBeforeBootstrap();

if (environment.production) {
  enableProdMode();
}

platformBrowserDynamic().bootstrapModule(AppModule)
  .catch(err => console.error(err));
